import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faPlay, faWrench } from '@/fontawesome';
import { handleAPIError, showToast } from '@/utils/toasts';
import { APIRoutes } from '@/services/api.routes';
import Modal from '@/components/ui/Modal';
import type { Tool, JsonSchema } from '@/modelsLocal/tool';

interface ToolExecutionModalProps {
  tool: Tool;
  onClose: () => void;
}

interface FormField {
  name: string;
  value: unknown;
  type: string;
  required: boolean;
  description: string;
  enum?: string[];
  properties?: FormField[];
}

export default function ToolExecutionModal({ tool, onClose }: ToolExecutionModalProps) {
  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const [formFields, setFormFields] = useState<FormField[]>([]);
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState<unknown>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Initialize form fields from tool parameters schema
    const fields = buildFormFields(tool.parameters);
    setFormFields(fields);
    
    // Initialize form data with default values
    const initialData: Record<string, unknown> = {};
    fields.forEach(field => {
      initialData[field.name] = field.value || '';
    });
    setFormData(initialData);
  }, [tool]);

  const buildFormFields = (schema: JsonSchema): FormField[] => {
    if (!schema.properties) {
      return [];
    }

    const requiredFields = schema.required || [];
    
    return Object.entries(schema.properties).map(([propertyName, propertySchema]) => {
      const typedPropertySchema = propertySchema as JsonSchema;
      const field: FormField = {
        name: propertyName,
        value: typedPropertySchema.default || '',
        type: typedPropertySchema.type || 'string',
        required: requiredFields.includes(propertyName),
        description: typedPropertySchema.description || '',
        enum: typedPropertySchema.enum
      };

      if (typedPropertySchema.properties) {
        field.properties = buildFormFields(typedPropertySchema);
      }

      return field;
    });
  };

  const handleInputChange = (fieldName: string, value: unknown) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    const missingFields = formFields.filter(field => 
      field.required && (formData[field.name] === '' || formData[field.name] === null || formData[field.name] === undefined)
    );
    
    if (missingFields.length > 0) {
      setError(`Bitte füllen Sie alle erforderlichen Felder aus: ${missingFields.map(f => f.name).join(', ')}`);
      return;
    }

    try {
      setExecuting(true);
      setError(null);
      setResult(null);

      const response = await APIRoutes.executeTool({
        toolName: tool.name,
        arguments: formData
      });

      if (response.error) {
        setError(response.error);
      } else {
        setResult(response.result);
        showToast('Tool erfolgreich ausgeführt', 'success');
      }
    } catch (err) {
      setError('Fehler bei der Tool-Ausführung');
      handleAPIError(err);
    } finally {
      setExecuting(false);
    }
  };

  const renderFormField = (field: FormField) => {
    const fieldId = `field-${field.name}`;

    switch (field.type) {
      case 'boolean':
        return (
          <div key={field.name} className="mb-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={Boolean(formData[field.name])}
                onChange={(e) => handleInputChange(field.name, e.target.checked)}
                className="mr-2"
              />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {field.name}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </span>
            </label>
            {field.description && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{field.description}</p>
            )}
          </div>
        );

      case 'number':
      case 'integer':
        return (
          <div key={field.name} className="mb-4">
            <label htmlFor={fieldId} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {field.name}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              type="number"
              id={fieldId}
              value={String(formData[field.name] || '')}
              onChange={(e) => handleInputChange(field.name, e.target.value ? Number(e.target.value) : '')}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              required={field.required}
            />
            {field.description && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{field.description}</p>
            )}
          </div>
        );

      case 'string':
      default:
        if (field.enum) {
          return (
            <div key={field.name} className="mb-4">
              <label htmlFor={fieldId} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {field.name}
                {field.required && <span className="text-red-500 ml-1">*</span>}
              </label>
              <select
                id={fieldId}
                value={String(formData[field.name] || '')}
                onChange={(e) => handleInputChange(field.name, e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                required={field.required}
              >
                <option value="">Wählen Sie eine Option</option>
                {field.enum.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
              {field.description && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{field.description}</p>
              )}
            </div>
          );
        }

        return (
          <div key={field.name} className="mb-4">
            <label htmlFor={fieldId} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {field.name}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              type="text"
              id={fieldId}
              value={String(formData[field.name] || '')}
              onChange={(e) => handleInputChange(field.name, e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              required={field.required}
            />
            {field.description && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{field.description}</p>
            )}
          </div>
        );
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={`Tool ausführen: ${tool.name}`}
      icon={<FontAwesomeIcon icon={faWrench} className="w-5 h-5 text-orange-600 dark:text-orange-400" />}
      maxWidth="4xl"
      closeDisabled={executing}
    >
      <div className="p-6">
        <div className="mb-6">
          <p className="text-gray-600 dark:text-gray-400">{tool.description}</p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {formFields.map(field => renderFormField(field))}
          </div>

          {error && (
            <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
            </div>
          )}

          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={executing}
              className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 disabled:opacity-50"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={executing}
              className="px-6 py-2 bg-orange-600 hover:bg-orange-700 disabled:bg-orange-400 text-white rounded-lg transition-colors flex items-center"
            >
              {executing ? (
                <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <FontAwesomeIcon icon={faPlay} className="w-4 h-4 mr-2" />
              )}
              {executing ? 'Ausführen...' : 'Ausführen'}
            </button>
          </div>
        </form>

        {result !== null && (
          <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
              Ergebnis:
            </h3>
            <pre className="text-sm text-gray-700 dark:text-gray-300 overflow-x-auto whitespace-pre-wrap">
              {JSON.stringify(result as Record<string, unknown>, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </Modal>
  );
}